import { useState, useEffect } from 'react';
import useValidationDialog from './useValidationDialog';

// Global event system for clearing all validation hooks
let validationHookClearCallbacks = [];

export const clearAllValidationHooks = () => {
  validationHookClearCallbacks.forEach(callback => callback());
};

const registerValidationHookClearCallback = (callback) => {
  validationHookClearCallbacks.push(callback);
  return () => {
    validationHookClearCallbacks = validationHookClearCallbacks.filter(cb => cb !== callback);
  };
};

/**
 * Enhanced form validation hook that handles validation dialog + red borders
 *
 * @param {object} values - Form values from Redux/state
 * @param {boolean} isWBSEValid - WBSE validation state from Redux (optional)
 * @param {string} selectedEnvironment - Selected environment for conditional field validation (optional)
 * @returns {object} Validation functions and state
 */
export const useFormValidationWithBorders = (values, isWBSEValid = true, selectedEnvironment = null) => {
  const [rememberedErrors, setRememberedErrors] = useState([]);
  const { validationErrors, showValidationDialog, showValidationErrors, showSuccess, closeDialog } = useValidationDialog();

  // Register this hook instance for global clearing
  useEffect(() => {
    const clearCallback = () => {
      setRememberedErrors([]);
    };

    const unregister = registerValidationHookClearCallback(clearCallback);
    return unregister;
  }, []);

  // Apply red borders to fields that failed validation (persists across step navigation)
  useEffect(() => {
    if (rememberedErrors.length > 0) {
      const timer = setTimeout(() => {
        rememberedErrors.forEach(error => {
          const fieldname = error.split(' (')[0]; // Remove error descriptions
          try {
            const fieldElement = document.getElementById(fieldname);
            if (fieldElement && fieldElement.parentElement && fieldElement.parentElement.parentElement) {
              const fieldset = fieldElement.parentElement.parentElement.querySelector('fieldset');

              if (fieldset) {
                // Special handling for WBSE code (empty OR API validation failed)
                if (fieldname === "WBSEcode" && (!values[fieldname] || values[fieldname] === "" || !isWBSEValid)) {
                  fieldset.style.border = "1px solid #E20000";
                }
                // Special handling for TentativeServiceEndDate (requires environment selection)
                else if (fieldname === "TentativeServiceEndDate" && selectedEnvironment !== null && (!selectedEnvironment || selectedEnvironment === "")) {
                  fieldset.style.border = "1px solid #E20000";
                }
                // Regular empty field handling for all fields
                else if ((!values[fieldname] || values[fieldname] === "")) {
                  fieldset.style.border = "1px solid #E20000";
                }
              }
            }
          } catch (error) {
            // Field not currently visible/rendered
          }
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }); 

  // Automatically clear red borders when field values change or become valid
  useEffect(() => {
    if (rememberedErrors.length > 0) {
      rememberedErrors.forEach(error => {
        const fieldname = error.split(' (')[0]; // Remove error descriptions
        try {
          const fieldElement = document.getElementById(fieldname);
          if (fieldElement && fieldElement.parentElement && fieldElement.parentElement.parentElement) {
            const fieldset = fieldElement.parentElement.parentElement.querySelector('fieldset');
            if (fieldset) {
              // Clear red border if field now has a value or is valid
              const fieldValue = values[fieldname];
              const hasValue = fieldValue && fieldValue !== "";

              // Special handling for WBSE - clear if valid via API
              if (fieldname === "WBSEcode" && hasValue && isWBSEValid) {
                fieldset.style.border = "";
              }
              // Special handling for TentativeServiceEndDate - clear if environment is selected
              else if (fieldname === "TentativeServiceEndDate" && selectedEnvironment !== null && selectedEnvironment && selectedEnvironment !== "") {
                fieldset.style.border = "";
              }
              // Clear for other fields if they have values
              else if (fieldname !== "WBSEcode" && fieldname !== "TentativeServiceEndDate" && hasValue) {
                fieldset.style.border = "";
              }
            }
          }
        } catch (error) {
          // Field not currently visible
        }
      });
    }
  }, [values, isWBSEValid, selectedEnvironment, rememberedErrors]);

  /**
   * Enhanced validation result handler
   * Handles both validation dialog and red border logic
   *
   * @param {boolean} ispass - Whether validation passed
   * @param {array} missingFields - Array of missing field names
   * @param {function} formClickSubmit - Form submission function to call on success
   */
  const handleValidationResult = (ispass, missingFields, formClickSubmit) => {
    if (!ispass) {
      // Remember the validation errors for applying red borders later
      setRememberedErrors(missingFields);
      showValidationErrors(missingFields);
    } else {
      // Clear remembered errors on successful validation
      setRememberedErrors([]);
      showSuccess();
      if (formClickSubmit) {
        formClickSubmit();
      }
    }
  };

  /**
   * Clear all remembered validation errors (useful for form reset)
   */
  const clearValidationErrors = () => {
    setRememberedErrors([]);
  };

  return {
    // Dialog state
    validationErrors,
    showValidationDialog,
    closeDialog,

    // Main validation function
    handleValidationResult,

    // Utility functions
    clearValidationErrors,

    // For advanced use cases
    rememberedErrors,

    // Hook instance reference for external clearing
    setRememberedErrors
  };
};

export default useFormValidationWithBorders;